/**
 * 보안 파일 업로드 컴포넌트 (개선된 UI)
 */

import React, { useState, useRef } from 'react';
import { Modal, Button } from '../UI';
import { 
  Upload, 
  X, 
  Shield, 
  FileText, 
  AlertCircle,
  File,
  Image,
  FileArchive,
  Film,
  Music,
  Tag,
  Folder,
  Lock,
  Info
} from 'lucide-react';
import './SecureFileUpload.css';

function SecureFileUpload({ isOpen, onClose, onUpload }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [metadata, setMetadata] = useState({
    category: 'general',
    description: '',
    tags: [],
    enablePin: false
  });
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [showPinInput, setShowPinInput] = useState(false);
  const fileInputRef = useRef(null);

  // 파일 아이콘 선택
  const getFileIcon = (file) => {
    if (!file) return File;
    const type = file.type;
    
    if (type.startsWith('image/')) return Image;
    if (type.includes('pdf')) return FileText;
    if (type.includes('zip') || type.includes('rar')) return FileArchive;
    if (type.includes('video')) return Film;
    if (type.includes('audio')) return Music;
    if (type.includes('text') || type.includes('document')) return FileText;
    
    return File;
  };

  // 파일 선택 처리
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  // 파일 유효성 검사 및 설정
  const validateAndSetFile = (file) => {
    const MAX_SIZE = 50 * 1024 * 1024; // 50MB
    
    if (file.size > MAX_SIZE) {
      setError('파일 크기는 50MB 이하여야 합니다.');
      return;
    }

    setSelectedFile(file);
    setError('');
  };

  // 드래그 앤 드롭 처리
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  // 태그 추가
  const handleAddTag = () => {
    if (tagInput.trim() && !metadata.tags.includes(tagInput.trim())) {
      setMetadata(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  // 태그 제거
  const handleRemoveTag = (tag) => {
    setMetadata(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  // PIN 토글 (Master PIN 사용)
  const handlePinToggle = () => {
    setShowPinInput(!showPinInput);
    setMetadata(prev => ({
      ...prev,
      enablePin: !showPinInput
    }));
  };

  // 업로드 처리
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('파일을 선택해주세요.');
      return;
    }

    // Master PIN 보호 설정 확인
    // 실제 PIN 입력은 필요없음 - isPinProtected 플래그만 설정

    try {
      await onUpload(selectedFile, {
        category: metadata.category,
        description: metadata.description,
        tags: metadata.tags,
        isPinProtected: metadata.enablePin // Master PIN 플래그만 전달
      });
      
      // 초기화
      setSelectedFile(null);
      setMetadata({
        category: 'general',
        description: '',
        tags: [],
        enablePin: false
      });
      setShowPinInput(false);
      setError('');
    } catch (err) {
      setError(err.message || '파일 업로드에 실패했습니다.');
    }
  };

  // 파일 크기 포맷
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const FileIcon = selectedFile ? getFileIcon(selectedFile) : Upload;

  // 카테고리 목록
  const categories = [
    { value: 'general', label: '일반', icon: Folder },
    { value: 'document', label: '문서', icon: FileText },
    { value: 'image', label: '이미지', icon: Image },
    { value: 'finance', label: '금융', icon: FileText },
    { value: 'personal', label: '개인', icon: Lock },
    { value: 'work', label: '업무', icon: Folder },
    { value: 'other', label: '기타', icon: File }
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="보안 파일 업로드" size="md">
      <div className="secure-file-upload">
        {/* 파일 선택 영역 */}
        <div 
          className={`upload-drop-zone ${isDragging ? 'dragging' : ''} ${selectedFile ? 'has-file' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          
          <div className="drop-zone-content">
            <FileIcon size={selectedFile ? 48 : 64} />
            {selectedFile ? (
              <>
                <p className="file-name">{selectedFile.name}</p>
                <p className="file-size">{formatFileSize(selectedFile.size)}</p>
                <button 
                  className="change-file-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  다른 파일 선택
                </button>
              </>
            ) : (
              <>
                <p className="upload-text">
                  파일을 드래그하거나 클릭하여 선택하세요
                </p>
                <p className="upload-hint">
                  최대 50MB까지 업로드 가능합니다
                </p>
              </>
            )}
          </div>
        </div>

        {/* 파일 정보 입력 */}
        <div className="upload-metadata">
          {/* 카테고리 선택 */}
          <div className="form-group">
            <label>
              <Folder size={14} />
              카테고리
            </label>
            <select
              value={metadata.category}
              onChange={(e) => setMetadata(prev => ({ ...prev, category: e.target.value }))}
              className="form-select"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* 설명 입력 */}
          <div className="form-group">
            <label>
              <Info size={14} />
              설명 (선택사항)
            </label>
            <textarea
              placeholder="파일에 대한 설명을 입력하세요..."
              value={metadata.description}
              onChange={(e) => setMetadata(prev => ({ ...prev, description: e.target.value }))}
              className="form-textarea"
              rows="3"
            />
          </div>

          {/* 태그 입력 */}
          <div className="form-group">
            <label>
              <Tag size={14} />
              태그 (선택사항)
            </label>
            <div className="tag-input-wrapper">
              <input
                type="text"
                placeholder="태그 입력 후 Enter"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                className="form-input"
              />
              <button 
                type="button"
                onClick={handleAddTag}
                className="tag-add-btn"
                disabled={!tagInput.trim()}
              >
                추가
              </button>
            </div>
            {metadata.tags.length > 0 && (
              <div className="tags-list">
                {metadata.tags.map((tag, index) => (
                  <span key={index} className="tag">
                    {tag}
                    <button onClick={() => handleRemoveTag(tag)}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* PIN 보호 설정 */}
          <div className="form-group">
            <div className="pin-toggle-wrapper">
              <button
                type="button"
                className={`pin-toggle-btn ${showPinInput ? 'active' : ''}`}
                onClick={handlePinToggle}
              >
                <Shield size={16} />
                {showPinInput ? 'PIN 보호중' : 'PIN 보호하기'}
              </button>
              <span className="pin-hint">
                {showPinInput
                  ? 'Master PIN으로 보호됩니다'
                  : '클릭하여 PIN 보호 설정'}
              </span>
            </div>

            {showPinInput && (
              <div className="pin-input-section">
                <p className="pin-info">
                  <Info size={12} />
                  이 파일은 Master PIN으로 보호됩니다.
                  <br />
                  파일 다운로드, 미리보기, 삭제 시 Master PIN 입력이 필요합니다.
                </p>
                {!metadata.enablePin && (
                  <p className="pin-warning" style={{
                    marginTop: '12px',
                    padding: '8px 12px',
                    background: 'rgba(255, 193, 7, 0.1)',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                    color: 'var(--text-secondary)',
                    fontSize: '13px'
                  }}>
                    <AlertCircle size={14} style={{ marginRight: '6px', verticalAlign: 'text-bottom' }} />
                    Master PIN이 설정되어 있지 않으면 설정 페이지에서 먼저 설정하세요.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="upload-error">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {/* 액션 버튼 */}
        <div className="upload-actions">
          <Button variant="ghost" onClick={onClose}>
            취소
          </Button>
          <Button 
            variant="primary" 
            onClick={handleUpload}
            disabled={!selectedFile}
          >
            <Upload size={16} />
            업로드
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default SecureFileUpload;